import { useState } from 'react';
import { TableRow, TableCell, IconButton } from '@material-ui/core';
import CreateIcon from '@mui/icons-material/Create';
import DeleteIcon from '@mui/icons-material/Delete';

import { useStoreActions } from '~/store/hooks';
import { isoToFormat } from '~/utils/date';
import { extractErrorMessage } from '~/utils/error';
import { getEditValues } from '../form';
import ConfirmModal from '~/ui/components/common/ConfirmModal';
import ReadMoreLess from '~/ui/components/common/ReadMoreLess';
import HospitalizationModal from '../../popups/HospitalizationModal';

import { Timezone } from '~/types';
import { ICarePlanHospitalization } from '~/services/api/carePlanHospitalizations/types';
import { IFormValues } from '../../popups/HospitalizationModal/form/types';
import { IPagePermissions } from '../../../types';
import styles from './Item.module.scss';

interface IProps {
  item: ICarePlanHospitalization;
  permissions: IPagePermissions;
  onCarePlanChange: () => void;
}

const Item = ({ item, permissions, onCarePlanChange }: IProps) => {
  const [showEdit, setShowEdit] = useState(false);
  const [showRemove, setShowRemove] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const { onUpdateCarePlanHospitalization, onRemoveCarePlanHospitalization } = useStoreActions(
    actions => actions.carePlanHospitalizations,
  );
  const { showError } = useStoreActions(actions => actions.snackbar);

  const handleUpdateHospitalization = async (values: IFormValues) => {
    const payload = {
      ...values,
      id: item.id,
      date: isoToFormat(values.date, 'y-MM-dd'),
    };

    try {
      setIsUpdating(true);
      await onUpdateCarePlanHospitalization(payload);
      onCarePlanChange();
      setShowEdit(false);
    } catch (e) {
      showError(extractErrorMessage(e));
    } finally {
      setIsUpdating(false);
    }
  };

  const handleRemoveHospitalization = async () => {
    try {
      setIsRemoving(true);
      await onRemoveCarePlanHospitalization(item.id);
      onCarePlanChange();
    } catch (e) {
      showError(extractErrorMessage(e));
      setIsRemoving(false);
    }
  };

  return (
    <>
      <TableRow>
        <TableCell>{isoToFormat(item.date, 'dd MMM y', Timezone.GMT, false)}</TableCell>
        <TableCell>
          <ReadMoreLess content={item.reason} />
        </TableCell>
        {permissions.canManage && (
          <TableCell align="right">
            <IconButton
              className={styles.iconButton}
              title="Edit"
              onClick={() => setShowEdit(true)}
            >
              <CreateIcon className={styles.icon} />
            </IconButton>
            <IconButton
              className={styles.iconButton}
              title="Remove"
              onClick={() => setShowRemove(true)}
            >
              <DeleteIcon className={styles.icon} />
            </IconButton>
          </TableCell>
        )}
      </TableRow>
      {showEdit && (
        <HospitalizationModal
          mode="edit"
          initialValues={getEditValues(item)}
          isProcessing={isUpdating}
          onSubmit={handleUpdateHospitalization}
          onClose={() => setShowEdit(false)}
        />
      )}
      {showRemove && (
        <ConfirmModal
          isLoading={isRemoving}
          title="Remove Hospitalization"
          description="Are you sure you want to remove current Hospitalization?"
          onClose={() => setShowRemove(false)}
          onConfirm={handleRemoveHospitalization}
        />
      )}
    </>
  );
};

export default Item;
